import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../context/ThemeContext';
import { 
    ChevronRight, ChevronLeft, Maximize, Minimize, X,
    Shield, Server, Zap, Globe, MapPin,
    CheckCircle2, Building2, Landmark, Users,
    ArrowRight, Sun, Moon, Database, Lock, Activity,
    Radio, Briefcase, Cpu, Network, RefreshCw,
    Layers, Eye, BarChart3, Mail, Phone, Smartphone,
    Flag, Rocket, TrendingUp, Award, Cloud, Bot,
    Wand2, Code2, HardDrive, Brain, Sparkles,
    CreditCard, PiggyBank, FileCheck, ShieldCheck,
    Mountain, Target, ArrowUp
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const LaunchEvent = () => {
    const { theme, toggleTheme } = useTheme();
    const isDarkMode = theme === 'dark';
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const totalSlides = 14;

    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'ArrowRight':
            case ' ':
                setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1));
                break;
            case 'ArrowLeft':
                setCurrentSlide(prev => Math.max(prev - 1, 0));
                break;
            case 'Home':
                setCurrentSlide(0);
                break;
            case 'End':
                setCurrentSlide(totalSlides - 1);
                break;
            case 'f':
            case 'F':
                toggleFullscreen();
                break;
            case 'Escape':
                if (isFullscreen) toggleFullscreen();
                break;
            default:
                break;
        }
    }, [isFullscreen, totalSlides]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const goToHome = () => navigate('/');

    const slides = [
        WelcomeSlide,
        DNSOverviewSlide,
        DNSServicesSlide,
        PartnersSlide,
        HeroSlide,
        ByTheNumbersSlide,
        WhyEverestCloudSlide,
        BasecampToSummitSlide,
        AIInfrastructureSlide,
        BFSISlide,
        FintechSlide,
        AvailabilityZonesSlide,
        SecuritySlide,
        ThankYouSlide
    ];

    const SlideComponent = slides[currentSlide];
    const controlBgClass = isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-white/80 hover:bg-white';
    const controlBorderClass = isDarkMode ? 'border-white/10' : 'border-slate-200';

    return (
        <div className={`h-screen w-screen overflow-hidden ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
            {/* Slide Content */}
            <motion.div
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="h-full w-full"
            >
                <SlideComponent isDarkMode={isDarkMode} />
            </motion.div>

            {/* Navigation Dots */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-50">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setCurrentSlide(i)}
                        className={`h-2 rounded-full transition-all ${currentSlide === i ? 'w-8 bg-amber-500' : `w-2 ${isDarkMode ? 'bg-white/30 hover:bg-white/50' : 'bg-slate-300 hover:bg-slate-400'}`}`}
                    />
                ))}
            </div>

            {/* Top Controls */}
            <div className="absolute top-6 left-6 z-50">
                <Link to="/" className="flex items-center gap-3 group">
                    <img src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png" alt="EverestCloud" className="h-8 w-8 object-contain" />
                    <span className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Everest<span className="text-amber-500">Cloud</span>
                    </span>
                </Link>
            </div>

            <div className="absolute top-6 right-6 flex items-center gap-3 z-50">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {currentSlide + 1} / {totalSlides}
                </span>
                <button onClick={toggleTheme} className={`p-2 rounded-lg border ${controlBgClass} ${controlBorderClass} transition-all`}>
                    {isDarkMode ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-slate-600" />}
                </button>
                <button onClick={toggleFullscreen} className={`p-2 rounded-lg border ${controlBgClass} ${controlBorderClass} transition-all`}>
                    {isFullscreen ? <Minimize className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} /> : <Maximize className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />}
                </button>
                <button onClick={goToHome} className={`p-2 rounded-lg border ${controlBgClass} ${controlBorderClass} transition-all`}>
                    <X className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
                </button>
            </div>

            {/* Navigation Arrows */}
            {currentSlide > 0 && (
                <button onClick={() => setCurrentSlide(prev => prev - 1)}
                    className={`absolute left-6 top-1/2 -translate-y-1/2 p-3 rounded-full border ${controlBgClass} ${controlBorderClass} transition-all z-50`}>
                    <ChevronLeft className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
                </button>
            )}
            {currentSlide < totalSlides - 1 && (
                <button onClick={() => setCurrentSlide(prev => prev + 1)}
                    className={`absolute right-6 top-1/2 -translate-y-1/2 p-3 rounded-full border ${controlBgClass} ${controlBorderClass} transition-all z-50`}>
                    <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
                </button>
            )}

            {/* Keyboard hints */}
            <div className={`absolute bottom-8 right-6 text-xs ${isDarkMode ? 'text-slate-600' : 'text-slate-400'} z-50`}>
                Arrow Keys Navigate | F Fullscreen | ESC Exit
            </div>
        </div>
    );
};

// ==================== HELPER COMPONENTS ====================
const SlideTitle = ({ children, isDarkMode }) => (
    <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className={`text-5xl md:text-6xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-4 text-center`}>
        {children}
    </motion.h2>
);

const SlideSubtitle = ({ children, isDarkMode }) => (
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className={`text-xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} text-center mb-14`}>
        {children}
    </motion.p>
);

const AnimatedCounter = ({ value, suffix = '', isDarkMode }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        const num = parseFloat(value);
        const duration = 1500;
        const steps = 40;
        const increment = num / steps;
        let current = 0;
        const timer = setInterval(() => {
            current += increment;
            if (current >= num) { setCount(num); clearInterval(timer); }
            else setCount(Math.floor(current * 10) / 10);
        }, duration / steps);
        return () => clearInterval(timer);
    }, [value]);
    return <span>{count % 1 === 0 ? Math.floor(count) : count.toFixed(1)}{suffix}</span>;
};

// ==================== SLIDE 1: WELCOME ====================
const WelcomeSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16 relative">
        <div className="absolute inset-0 bg-cover bg-no-repeat opacity-30" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_everestcloud/artifacts/spue5mbw_backgroundjpeg.jpg')", backgroundPosition: "center 30%" }} />
        <div className="text-center max-w-5xl relative z-10">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="mb-4 -mt-24">
                <img src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png" alt="Digital Network Solution" className="h-24 w-24 mx-auto object-contain" />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className={`text-5xl md:text-7xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-6 leading-tight`}>
                Welcome to <span className="text-amber-500">EverestCloud</span>
            </motion.h1>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="mb-6">
                <p className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} max-w-3xl mx-auto leading-relaxed`}>
                    A Sovereign Cloud Platform by <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Digital Network Solution</span>
                </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }} 
                className={`inline-flex items-center gap-3 px-6 py-3 rounded-full ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'} mb-8`}>
                <Globe className="text-amber-500 w-5 h-5" />
                <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Nepal's Premier System Integrator & Cloud Service Provider</span>
            </motion.div>
            {/* Powered by Partners */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
                className="mt-6 flex items-center justify-center">
                <div className="px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 via-purple-600 to-green-500">
                    <span className="text-white/80 text-sm uppercase tracking-wider mr-3">Powered by</span>
                    <span className="text-white font-bold text-lg">CISCO • NUTANIX • INTEL • NVIDIA</span>
                </div>
            </motion.div>
        </div>
    </section>
);

// ==================== SLIDE 2: DNS COMPANY OVERVIEW ====================
const DNSOverviewSlide = ({ isDarkMode }) => {
    const milestones = [
        { num: '01', title: 'Company', desc: 'Founded 18 years ago, we have established a strong presence in the IT industry.', color: 'rose' },
        { num: '02', title: 'Certifications', desc: 'Certified with ISO 9001:2015 and 27001:2013, ensuring quality and security standards.', color: 'teal' },
        { num: '03', title: 'Team Size', desc: 'Our team consists of 180+ professionals, primarily focusing on technical expertise.', color: 'amber' },
        { num: '04', title: 'Service Type', desc: 'We operate as a System Integrator and Cloud Service Provider, delivering comprehensive IT services.', color: 'cyan' },
        { num: '05', title: 'Technical Expertise', desc: 'The majority of our workforce are technical staff, enhancing service delivery and innovation.', color: 'emerald' },
        { num: '06', title: 'Company Mission', desc: 'We aim to deliver high-quality IT solutions that meet the evolving needs of our clients.', color: 'blue' },
    ];
    const colorMap = {
        rose: { bg: 'bg-rose-500' }, teal: { bg: 'bg-teal-500' }, amber: { bg: 'bg-amber-500' },
        cyan: { bg: 'bg-cyan-500' }, emerald: { bg: 'bg-emerald-500' }, blue: { bg: 'bg-blue-500' },
    };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className={`text-4xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2`}>
                    <span className="text-amber-500">Digital Network Solution</span> Company Overview
                </motion.h2>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                    className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-10`}>
                    An Insight into our Journey
                </motion.p>
                
                <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                    {milestones.map((item, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                            className="flex items-start gap-4">
                            <div className={`${colorMap[item.color].bg} text-white font-bold text-sm px-3 py-1 rounded-md shrink-0`}>
                                {item.num}
                            </div>
                            <div>
                                <h3 className={`font-bold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.title}</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
                    className="mt-10 flex justify-center gap-8">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                        <Shield className="w-4 h-4 text-emerald-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>ISO 9001:2015</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
                        <Lock className="w-4 h-4 text-blue-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>ISO 27001:2013</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                        <Users className="w-4 h-4 text-amber-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>180+ Team Members</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 3: DNS SERVICES ====================
const DNSServicesSlide = ({ isDarkMode }) => {
    const services = [
        { icon: Wand2, title: 'Consulting & Digital Transformation', desc: 'Strategic guidance for navigating the digital landscape.' },
        { icon: Cloud, title: 'Private Cloud & Hybrid Services', desc: 'Scalable, secure, and managed cloud infrastructure.' },
        { icon: Bot, title: 'AI, AIOps & Automation', desc: 'Intelligent operations for the modern enterprise.' },
        { icon: Shield, title: 'SIEM, SOC & Cybersecurity', desc: 'Comprehensive threat detection and response.' },
        { icon: Network, title: 'Data Center Network & Security', desc: 'High-performance networking for critical workloads.' },
        { icon: Code2, title: 'Kubernetes & Modernization', desc: 'Containerization for cloud-native agility.' },
        { icon: HardDrive, title: 'Infrastructure Backup & Recovery', desc: 'Ironclad data protection strategies.' },
        { icon: Briefcase, title: 'Digital Workplace & Analytics', desc: 'Empowering the hybrid workforce.' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Enterprise-Grade <span className="text-amber-500">Ecosystem</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>End-to-end capabilities designed for mission-critical environments.</SlideSubtitle>
                
                <div className="grid grid-cols-4 gap-5">
                    {services.map((service, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
                            className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-blue-500/30' : 'bg-slate-50 border-slate-200 hover:border-blue-300'} transition-all group`}>
                            <div className={`w-10 h-10 rounded-xl ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                <service.icon className="w-5 h-5 text-blue-500" />
                            </div>
                            <h3 className={`font-bold text-sm mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'} leading-tight`}>{service.title}</h3>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{service.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 4: PARTNERS ====================
const PartnersSlide = ({ isDarkMode }) => {
    const partners = [
        { name: 'Cisco', tier: 'PREMIER PARTNER', desc: 'Networking, security, and collaboration solutions powering enterprise infrastructure.', tags: ['Enterprise Networking', 'Security'], color: 'blue' },
        { name: 'Nutanix', tier: 'PREMIER PARTNER', desc: 'Hyperconverged infrastructure delivering cloud-like simplicity for on-premises.', tags: ['HCI Platform', 'Cloud Management'], color: 'green' },
        { name: 'Intel', tier: 'TECHNOLOGY PARTNER', desc: 'Latest generation Xeon processors powering our compute infrastructure.', tags: ['Xeon Scalable', 'Optane'], color: 'cyan' },
        { name: 'NVIDIA', tier: 'TECHNOLOGY PARTNER', desc: 'GPU acceleration for AI/ML workloads and high-performance computing.', tags: ['A100/H100 GPUs', 'CUDA'], color: 'green' },
        { name: 'Fortinet', tier: 'PARTNER', desc: 'Security Fabric covering network security, SD-WAN, and secure access.', tags: ['FortiGate', 'SD-WAN'], color: 'red' },
        { name: 'Palo Alto', tier: 'PARTNER', desc: 'Next-gen cybersecurity including firewalls and AI-driven threat detection.', tags: ['NGFW', 'Cortex XDR'], color: 'orange' },
        { name: 'Commvault', tier: 'PARTNER', desc: 'Enterprise backup and data management protecting critical assets.', tags: ['Data Protection', 'DR'], color: 'purple' },
        { name: 'CyberArk', tier: 'PARTNER', desc: 'Identity security and privileged access management.', tags: ['PAM', 'Identity'], color: 'blue' },
    ];

    const colorMap = {
        blue: 'text-blue-500', green: 'text-green-500', cyan: 'text-cyan-500',
        red: 'text-red-500', orange: 'text-orange-500', purple: 'text-purple-500'
    };

    return (
        <section className="h-full w-full flex items-center justify-center px-12 py-6">
            <div className="max-w-7xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Partner <span className="text-amber-500">Portfolio</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>World-class technology partnerships powering enterprise solutions</SlideSubtitle>
                
                <div className="grid grid-cols-4 gap-4">
                    {partners.map((partner, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.05 }}
                            className={`p-4 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-200 hover:border-slate-300'} transition-all`}>
                            <h3 className={`font-bold text-base mb-1 ${partner.tier.includes('PREMIER') || partner.tier.includes('TECHNOLOGY') ? colorMap[partner.color] : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                {partner.name}
                            </h3>
                            <span className={`text-[10px] font-bold tracking-wider ${partner.tier.includes('PREMIER') || partner.tier.includes('TECHNOLOGY') ? 'text-amber-500/70' : (isDarkMode ? 'text-slate-500' : 'text-slate-400')}`}>
                                {partner.tier}
                            </span>
                            <p className={`text-xs mt-2 mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} line-clamp-2`}>{partner.desc}</p>
                            <div className="flex flex-wrap gap-1">
                                {partner.tags.map((tag, j) => (
                                    <span key={j} className={`text-[10px] px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 5: HERO ====================
const HeroSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16 relative">
        <div className="absolute inset-0 bg-cover bg-no-repeat opacity-30" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_everestcloud/artifacts/spue5mbw_backgroundjpeg.jpg')", backgroundPosition: "center 30%" }} />
        <div className="text-center max-w-5xl relative z-10">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8">
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                    <span className="text-amber-500 text-sm font-bold tracking-[0.15em] uppercase">Nepal's Sovereign Cloud</span>
                </div>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className={`text-6xl md:text-8xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-8 leading-tight`}>
                Sovereign by Design.<br /><span className="text-amber-500">Resilient by Architecture.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} max-w-3xl mx-auto leading-relaxed`}>
                Enterprise cloud infrastructure built entirely within Nepal. Your data never leaves the country.
            </motion.p>
        </div>
    </section>
);

// ==================== SLIDE 6: BY THE NUMBERS ====================
const ByTheNumbersSlide = ({ isDarkMode }) => {
    const metrics = [
        { value: '99.9', suffix: '%', label: 'Uptime SLA', icon: Activity, color: 'emerald' },
        { value: '3', suffix: '', label: 'Availability Zones', icon: MapPin, color: 'amber' },
        { value: '40', suffix: '+', label: 'Compute Nodes', icon: Server, color: 'blue' },
        { value: '2.5', suffix: ' PB', label: 'NVMe Storage', icon: Database, color: 'purple' },
        { value: '240', suffix: ' Gbps', label: 'Backplane Capacity', icon: Network, color: 'cyan' },
        { value: '8', suffix: ' ms', label: 'Local Latency', icon: Zap, color: 'rose' },
    ];
    const colorMap = { emerald: 'text-emerald-500', amber: 'text-amber-500', blue: 'text-blue-500', purple: 'text-purple-500', cyan: 'text-cyan-500', rose: 'text-rose-500' };
    const bgMap = { emerald: 'bg-emerald-500/10', amber: 'bg-amber-500/10', blue: 'bg-blue-500/10', purple: 'bg-purple-500/10', cyan: 'bg-cyan-500/10', rose: 'bg-rose-500/10' };
    const borderMap = { emerald: 'border-emerald-500/20', amber: 'border-amber-500/20', blue: 'border-blue-500/20', purple: 'border-purple-500/20', cyan: 'border-cyan-500/20', rose: 'border-rose-500/20' };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>By the <span className="text-amber-500">Numbers</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Infrastructure that speaks for itself</SlideSubtitle>
                <div className="grid grid-cols-3 gap-6">
                    {metrics.map((m, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                            className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} relative overflow-hidden group hover:scale-[1.02] transition-transform`}>
                            <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full ${bgMap[m.color]} blur-2xl opacity-50 group-hover:opacity-80 transition-opacity`} />
                            <div className={`w-12 h-12 rounded-xl ${bgMap[m.color]} border ${borderMap[m.color]} flex items-center justify-center mb-4`}>
                                <m.icon className={`w-6 h-6 ${colorMap[m.color]}`} />
                            </div>
                            <div className={`text-4xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>
                                <AnimatedCounter value={m.value} suffix={m.suffix} isDarkMode={isDarkMode} />
                            </div>
                            <p className={`text-sm font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{m.label}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 7: WHY EVERESTCLOUD ====================
const WhyEverestCloudSlide = ({ isDarkMode }) => {
    const points = [
        { icon: Shield, title: "Data Sovereignty", desc: "100% Nepal-hosted infrastructure under local jurisdiction. No hyperscaler dependencies.", color: 'amber' },
        { icon: Lock, title: "Zero Foreign Access", desc: "No offshore data exposure. Compliant with NRB and national data policies.", color: 'red' },
        { icon: Activity, title: "Business Continuity", desc: "Multi-site architecture with automated failover. RPO=0, RTO<15 min.", color: 'emerald' },
        { icon: Zap, title: "Local Performance", desc: "Sub-10ms latency via NPIX direct peering. 5x faster than hyperscalers.", color: 'blue' },
    ];
    const colorMap = { amber: 'text-amber-500 bg-amber-500/10 border-amber-500/20', red: 'text-red-500 bg-red-500/10 border-red-500/20', emerald: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20', blue: 'text-blue-500 bg-blue-500/10 border-blue-500/20' };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Why <span className="text-amber-500">EverestCloud</span>?</SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Built for enterprises that demand control</SlideSubtitle>
                <div className="grid grid-cols-2 gap-6">
                    {points.map((p, i) => {
                        const [textC, bgC, borderC] = colorMap[p.color].split(' ');
                        return (
                            <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                                className={`p-7 rounded-2xl ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-slate-50 border-slate-200 hover:border-slate-300'} border transition-all group`}>
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl ${bgC} border ${borderC} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                                        <p.icon className={`w-6 h-6 ${textC}`} />
                                    </div>
                                    <div>
                                        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{p.title}</h3>
                                        <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{p.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 8: BASECAMP TO SUMMIT ====================
const BasecampToSummitSlide = ({ isDarkMode }) => {
    const journey = [
        { stage: 'Base Camp', altitude: '5,364m', title: 'Assessment & Planning', desc: 'Evaluate your current infrastructure and design cloud migration strategy', icon: Flag, color: 'blue' },
        { stage: 'Camp 1', altitude: '6,065m', title: 'Foundation Setup', desc: 'Deploy secure networking, identity management, and core services', icon: Layers, color: 'cyan' },
        { stage: 'Camp 2', altitude: '6,500m', title: 'Migration & Modernization', desc: 'Migrate workloads with zero downtime, containerize applications', icon: RefreshCw, color: 'emerald' },
        { stage: 'Camp 3', altitude: '7,200m', title: 'Optimization & Scale', desc: 'Auto-scaling, cost optimization, and performance tuning', icon: TrendingUp, color: 'amber' },
        { stage: 'Summit', altitude: '8,849m', title: 'Digital Leadership', desc: 'AI/ML integration, innovation at scale, competitive advantage', icon: Mountain, color: 'rose' },
    ];

    const colorMap = {
        blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-500' },
        cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-500' },
        emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-500' },
        amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-500' },
        rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-500' },
    };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>From <span className="text-amber-500">Base Camp</span> to Summit</SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Your journey to the top of the digital world</SlideSubtitle>
                
                <div className="relative">
                    {/* Journey Path */}
                    <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-emerald-500 to-rose-500 rounded-full" />
                    
                    <div className="space-y-4 pl-20">
                        {journey.map((step, i) => (
                            <motion.div key={i} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                                className={`relative p-5 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} group hover:scale-[1.01] transition-all`}>
                                {/* Connector dot */}
                                <div className={`absolute -left-[52px] top-1/2 -translate-y-1/2 w-5 h-5 rounded-full ${colorMap[step.color].bg} border-2 ${colorMap[step.color].border}`} />
                                
                                <div className="flex items-center gap-6">
                                    <div className={`w-14 h-14 rounded-xl ${colorMap[step.color].bg} border ${colorMap[step.color].border} flex items-center justify-center shrink-0`}>
                                        <step.icon className={`w-7 h-7 ${colorMap[step.color].text}`} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className={`text-sm font-bold ${colorMap[step.color].text}`}>{step.stage}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10 text-slate-400' : 'bg-slate-200 text-slate-600'}`}>{step.altitude}</span>
                                        </div>
                                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{step.title}</h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{step.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 9: AI INFRASTRUCTURE / GPU-as-a-Service ====================
const AIInfrastructureSlide = ({ isDarkMode }) => {
    const gpuOfferings = [
        { icon: Cpu, title: 'NVIDIA L4', desc: 'Efficient inference GPU for AI video, image generation, and LLM serving', stat: 'Available Now', available: true },
        { icon: Cpu, title: 'RTX PRO 6000 Blackwell', desc: 'Latest Blackwell architecture for professional AI/ML workloads and rendering', stat: 'Available Now', available: true },
        { icon: Cpu, title: 'NVIDIA H100', desc: 'Ultimate performance for large-scale AI training and HPC workloads', stat: 'Coming Soon', available: false },
    ];

    const capabilities = [
        { icon: Brain, title: 'ML Platforms', desc: 'Pre-configured TensorFlow, PyTorch, and CUDA environments', stat: 'MLOps Ready' },
        { icon: Database, title: 'Vector Databases', desc: 'High-performance storage for embeddings and AI models', stat: 'Sub-ms latency' },
        { icon: Sparkles, title: 'Private LLM Hosting', desc: 'Deploy and fine-tune LLMs with enterprise security', stat: 'Data stays local' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-green-900/20" />
            <div className="max-w-6xl w-full relative z-10">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
                    <Cpu className="w-4 h-4 text-green-500" />
                    <span className="text-green-500 text-sm font-bold uppercase tracking-wider">GPU-as-a-Service • In-Country</span>
                </motion.div>
                
                <SlideTitle isDarkMode={isDarkMode}>Nepal's First <span className="text-green-500">GPU Cloud</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Enterprise AI infrastructure with full data sovereignty — your models never leave Nepal</SlideSubtitle>
                
                {/* GPU Offerings */}
                <div className="grid grid-cols-3 gap-5 mb-6">
                    {gpuOfferings.map((gpu, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                            className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-green-500/30' : 'bg-slate-50 border-slate-200 hover:border-green-300'} transition-all group relative overflow-hidden`}>
                            {gpu.available && <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full blur-2xl" />}
                            <div className={`w-12 h-12 rounded-xl ${gpu.available ? 'bg-green-500/10 border-green-500/20' : 'bg-amber-500/10 border-amber-500/20'} border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                <gpu.icon className={`w-6 h-6 ${gpu.available ? 'text-green-500' : 'text-amber-500'}`} />
                            </div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{gpu.title}</h3>
                                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${gpu.available ? 'bg-green-500/20 text-green-500' : 'bg-amber-500/20 text-amber-500'}`}>
                                    {gpu.stat}
                                </span>
                            </div>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{gpu.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* Additional Capabilities */}
                <div className="grid grid-cols-3 gap-4">
                    {capabilities.map((cap, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.1 }}
                            className={`p-4 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} flex items-center gap-3`}>
                            <div className={`w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0`}>
                                <cap.icon className="w-5 h-5 text-purple-500" />
                            </div>
                            <div>
                                <h4 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{cap.title}</h4>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{cap.stat}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
                
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                    className="mt-6 text-center">
                    <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        <span className="text-green-500 font-semibold">Train locally, deploy securely</span> — First sovereign GPU cloud in Nepal
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 10: BFSI SLIDE ====================
const BFSISlide = ({ isDarkMode }) => {
    const bfsiFeatures = [
        { icon: Landmark, title: 'NRB Compliant', desc: 'Full compliance with Nepal Rastra Bank data localization mandates' },
        { icon: ShieldCheck, title: 'PCI-DSS Ready', desc: 'Infrastructure designed for payment card industry standards' },
        { icon: Lock, title: 'Data Encryption', desc: 'AES-256 at rest, TLS 1.3 in transit, HSM key management' },
        { icon: Activity, title: 'Real-time Processing', desc: 'Sub-millisecond latency for core banking transactions' },
        { icon: RefreshCw, title: 'DR/BCP', desc: 'Multi-AZ with RPO=0 for mission-critical banking systems' },
        { icon: Eye, title: '24/7 SOC', desc: 'Security Operations Center monitoring all banking workloads' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                    <Landmark className="w-4 h-4 text-emerald-500" />
                    <span className="text-emerald-500 text-sm font-bold uppercase tracking-wider">Banking & Financial Services</span>
                </motion.div>
                
                <SlideTitle isDarkMode={isDarkMode}>Built for <span className="text-emerald-500">BFSI</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Infrastructure trusted by 6 of Nepal's top 10 banks</SlideSubtitle>
                
                <div className="grid grid-cols-3 gap-5">
                    {bfsiFeatures.map((feature, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
                            className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-emerald-500/30' : 'bg-slate-50 border-slate-200 hover:border-emerald-300'} transition-all group`}>
                            <div className={`w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                <feature.icon className="w-6 h-6 text-emerald-500" />
                            </div>
                            <h3 className={`font-bold text-base mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h3>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 11: FINTECH SLIDE ====================
const FintechSlide = ({ isDarkMode }) => {
    const fintechUseCases = [
        { icon: CreditCard, title: 'Digital Payments', desc: 'High-throughput infrastructure for mobile wallets, QR payments, and card processing' },
        { icon: PiggyBank, title: 'Digital Lending', desc: 'Scalable compute for credit scoring, KYC automation, and loan management' },
        { icon: BarChart3, title: 'Trading Platforms', desc: 'Low-latency infrastructure for stock trading and financial analytics' },
        { icon: FileCheck, title: 'RegTech', desc: 'Compliance automation, AML screening, and regulatory reporting' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
                    <Rocket className="w-4 h-4 text-amber-500" />
                    <span className="text-amber-500 text-sm font-bold uppercase tracking-wider">Fintech Innovation</span>
                </motion.div>
                
                <SlideTitle isDarkMode={isDarkMode}>Powering Nepal's <span className="text-amber-500">Fintech</span> Revolution</SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Agile infrastructure for the next generation of financial services</SlideSubtitle>
                
                <div className="grid grid-cols-2 gap-6">
                    {fintechUseCases.map((useCase, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.15 }}
                            className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-amber-500/30' : 'bg-slate-50 border-slate-200 hover:border-amber-300'} transition-all group`}>
                            <div className="flex items-start gap-4">
                                <div className={`w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                                    <useCase.icon className="w-7 h-7 text-amber-500" />
                                </div>
                                <div>
                                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2`}>{useCase.title}</h3>
                                    <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{useCase.desc}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
                
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
                    className="mt-8 flex justify-center gap-6">
                    <div className={`px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <span className="text-2xl font-bold text-amber-500">100K+</span>
                        <span className={`ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>TPS Capacity</span>
                    </div>
                    <div className={`px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <span className="text-2xl font-bold text-amber-500">&lt;10ms</span>
                        <span className={`ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>API Latency</span>
                    </div>
                    <div className={`px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <span className="text-2xl font-bold text-amber-500">99.99%</span>
                        <span className={`ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Availability</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 12: AVAILABILITY ZONES ====================
const AvailabilityZonesSlide = ({ isDarkMode }) => {
    const zones = [
        { name: "KATHMANDU", code: "AZ-1", role: "Primary DC", color: "emerald", status: "Active", icon: Server },
        { name: "LALITPUR", code: "AZ-2", role: "Near DR", color: "amber", status: "Sync Replica", icon: RefreshCw },
        { name: "BUTWAL", code: "AZ-3", role: "Far DR", color: "blue", status: "Async Replica", icon: Database },
    ];
    const colorStyles = {
        emerald: { border: isDarkMode ? 'border-emerald-500/50' : 'border-emerald-500', bg: isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50', text: isDarkMode ? 'text-emerald-400' : 'text-emerald-600', dot: 'bg-emerald-500' },
        amber: { border: isDarkMode ? 'border-amber-500/50' : 'border-amber-500', bg: isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50', text: isDarkMode ? 'text-amber-400' : 'text-amber-600', dot: 'bg-amber-500' },
        blue: { border: isDarkMode ? 'border-blue-500/50' : 'border-blue-500', bg: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50', text: isDarkMode ? 'text-blue-400' : 'text-blue-600', dot: 'bg-blue-500' },
    };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Multi-AZ <span className="text-amber-500">Architecture</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Geographic redundancy across Nepal</SlideSubtitle>
                <div className="grid grid-cols-3 gap-8">
                    {zones.map((zone, i) => {
                        const style = colorStyles[zone.color];
                        return (
                            <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                                className={`p-8 rounded-2xl border-2 ${style.border} ${style.bg} text-center relative overflow-hidden group`}>
                                <div className={`absolute top-4 right-4 flex items-center gap-2`}>
                                    <span className={`w-2 h-2 rounded-full ${style.dot} animate-pulse`} />
                                    <span className={`text-xs font-medium ${style.text}`}>{zone.status}</span>
                                </div>
                                <div className={`w-16 h-16 mx-auto rounded-2xl ${style.bg} border ${style.border} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                    <zone.icon className={`w-8 h-8 ${style.text}`} />
                                </div>
                                <div className={`text-xs font-bold tracking-widest ${style.text} mb-2`}>{zone.code}</div>
                                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{zone.name}</h3>
                                <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{zone.role}</p>
                            </motion.div>
                        );
                    })}
                </div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                    className="mt-8 flex justify-center gap-6">
                    <div className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>RPO: <strong className="text-emerald-500">0</strong></span>
                    </div>
                    <div className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>RTO: <strong className="text-amber-500">&lt;15 min</strong></span>
                    </div>
                    <div className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Inter-AZ Latency: <strong className="text-blue-500">&lt;1ms</strong></span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 13: SECURITY ====================
const SecuritySlide = ({ isDarkMode }) => {
    const securityLayers = [
        { icon: Shield, title: "Zero Trust Architecture", desc: "Every request authenticated and authorized" },
        { icon: Lock, title: "End-to-End Encryption", desc: "AES-256 at rest, TLS 1.3 in transit" },
        { icon: Eye, title: "24/7 SOC Monitoring", desc: "Real-time threat detection and response" },
        { icon: Network, title: "Micro-segmentation", desc: "Network isolation at workload level" },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Enterprise <span className="text-red-500">Security</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Defense in depth for your most sensitive workloads</SlideSubtitle>
                <div className="grid grid-cols-2 gap-6">
                    {securityLayers.map((layer, i) => (
                        <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.1 }}
                            className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-red-500/30' : 'bg-slate-50 border-slate-200 hover:border-red-300'} transition-all group`}>
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                                    <layer.icon className="w-6 h-6 text-red-500" />
                                </div>
                                <div>
                                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{layer.title}</h3>
                                    <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{layer.desc}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 14: THANK YOU ====================
const ThankYouSlide = ({ isDarkMode }) => {
    const frontendUrl = process.env.REACT_APP_BACKEND_URL || 'https://everestcloud.com.np';
    const demoUrl = `${frontendUrl}/demo`;

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-5xl w-full text-center">
                <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
                    <img src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png" alt="EverestCloud" className="h-20 w-20 mx-auto mb-8 object-contain" />
                </motion.div>
                <motion.h2 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }}
                    className={`text-5xl md:text-6xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-6`}>
                    Let's Scale <span className="text-amber-500">Together</span>
                </motion.h2>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                    className={`text-xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-12 max-w-2xl mx-auto`}>
                    From base camp to the summit — EverestCloud is your partner in digital transformation
                </motion.p>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                    className="flex items-center justify-center gap-16">
                    <div className={`p-6 rounded-2xl ${isDarkMode ? 'bg-white' : 'bg-white border border-slate-200'}`}>
                        <QRCodeSVG value={demoUrl} size={150} level="H" includeMargin={true} />
                        <p className={`mt-3 text-sm font-medium ${isDarkMode ? 'text-slate-700' : 'text-slate-600'}`}>Scan for Demo</p>
                    </div>
                    <div className="text-left">
                        <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Get in Touch</h3>
                        <div className="space-y-4">
                            <div className={`flex items-center gap-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <Globe className="w-5 h-5 text-amber-500" />
                                <span>everestcloud.com.np</span>
                            </div>
                            <div className={`flex items-center gap-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <Mail className="w-5 h-5 text-amber-500" />
                                <span>support@digitalnetwork.com.np</span>
                            </div>
                            <div className={`flex items-center gap-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                <Phone className="w-5 h-5 text-amber-500" />
                                <span>+977-14537311</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Powered by Partners Footer */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
                    className="mt-12 pt-8 border-t border-white/10">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'} mb-3`}>Powered by</p>
                    <div className="flex items-center justify-center gap-8">
                        <span className={`font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>CISCO</span>
                        <span className={`font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>NUTANIX</span>
                        <span className={`font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>INTEL</span>
                        <span className={`font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>NVIDIA</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default LaunchEvent;
